import { useTranslation } from "react-i18next";
import type {
  EligibleShipment,
  PickupPreview,
} from "@/api/use-schedule-pickup";
import { SheetSection } from "./sheet-primitives";

export function PickupDeliveryConsequences({
  consequences,
  shipments,
}: {
  consequences: PickupPreview["deliveryConsequences"];
  shipments: readonly Pick<EligibleShipment, "id" | "shipmentNumber">[];
}) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const changes = consequences ?? [];
  if (!changes.length) return null;
  const instantLabel = (value: string | null, zone: string) =>
    value
      ? new Intl.DateTimeFormat(locale, {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: zone,
        }).format(new Date(value))
      : t("pickup.where.planNotSpecified");
  return (
    <SheetSection title={t("pickup.where.planningTitle")}>
      <p className="mb-3 text-xs text-default-600">
        {t("pickup.where.planningNotice")}
      </p>
      <div className="space-y-3 text-xs">
        {changes.map((change) => {
          const number = shipments.find(
            (shipment) => shipment.id === change.shipmentId
          )?.shipmentNumber;
          const planning = change.planning;
          // Missing consent must not be reconstructed from an existing visit or catalog.
          if (!planning)
            return (
              <p key={change.shipmentId} role="alert">
                {t("pickup.where.planningUnavailable")}
              </p>
            );
          const deliveryLabel = (value: string | null) =>
            !value
              ? t("pickup.where.deliveryUnknown")
              : planning.deliveredPrecision === "DATE"
                ? value.slice(0, 10)
                : instantLabel(value, planning.timing.timeZone);
          return (
            <section
              key={change.shipmentId}
              aria-label={`SHP-${number}`}
              className="space-y-2 rounded-medium border border-divider p-3"
            >
              <h4 className="font-semibold">SHP-{number}</h4>
              <dl className="space-y-1">
                <div>
                  <dt className="font-medium">
                    {t("pickup.where.previousEstimate")}
                  </dt>
                  <dd>
                    {change.previousEstimatedDelivery ??
                      t("pickup.where.deliveryUnknown")}
                  </dd>
                  <dd className="text-default-600">
                    {t("pickup.where.previousEstimateNotice")}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">
                    {t("pickup.where.proposedEarliestDelivery")}
                  </dt>
                  <dd>{deliveryLabel(change.estimatedDelivery)}</dd>
                </div>
                <div>
                  <dt className="font-medium">
                    {t("pickup.where.proposedWindow")}
                  </dt>
                  <dd>
                    {planning.timing.scheduledDate} ·{" "}
                    {planning.timing.startTime}–{planning.timing.endTime} ·{" "}
                    {planning.timing.timeZone}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">{t("pickup.where.readyAt")}</dt>
                  <dd>
                    {instantLabel(planning.readyAt, planning.timing.timeZone)}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">{t("pickup.where.handoffBy")}</dt>
                  <dd>
                    {instantLabel(planning.handoffBy, planning.timing.timeZone)}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">
                    {t("pickup.where.atCarrierFastest")}
                  </dt>
                  <dd>
                    {instantLabel(
                      planning.atCarrierFastest,
                      planning.timing.timeZone
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">
                    {t("pickup.where.latestDelivery")}
                  </dt>
                  <dd>{deliveryLabel(planning.deliveredSlowest)}</dd>
                </div>
              </dl>
            </section>
          );
        })}
      </div>
    </SheetSection>
  );
}
